'use client';
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Range } from 'react-range';
import { Manufacturer } from '@/types/product';
import { useFilterOptions, useSortOptions } from '@/hooks/useProductFilters';

interface ProductsFilterBarMobileProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (filters: any) => void;
  manufacturers?: Manufacturer[];
  sortBy?: string;
  selectedOptions?: string[];
  selectedManufacturer?: Manufacturer | null;
  priceRange?: [number, number];
}

const ProductsFilterBarMobile: React.FC<ProductsFilterBarMobileProps> = ({
  isOpen,
  onClose,
  onApply,
  manufacturers = [],
  sortBy: initialSortBy = '',
  selectedOptions: initialSelectedOptions = [],
  selectedManufacturer: initialSelectedManufacturer = null,
  priceRange: initialPriceRange = [0, 1000]
}) => {
  const [selectedCategories, setSelectedCategories] = useState<string[]>(initialSelectedOptions);
  const [selectedSort, setSelectedSort] = useState<string>(initialSortBy);
  const [priceRange, setPriceRange] = useState<[number, number]>(initialPriceRange);
  const [minPrice, setMinPrice] = useState(initialPriceRange[0].toString());
  const [maxPrice, setMaxPrice] = useState(initialPriceRange[1].toString());

  const { sortOptions } = useSortOptions();
  const { filterOptions } = useFilterOptions();  
  // Estados para marcas
  const [searchBrand, setSearchBrand] = useState('');
  const [filteredManufacturers, setFilteredManufacturers] = useState<Manufacturer[]>(manufacturers);
  const [selectedManufacturer, setSelectedManufacturer] = useState<Manufacturer | null>(initialSelectedManufacturer);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);


  // Sincronizar estados cuando cambian las props
  useEffect(() => {
    setSelectedCategories(initialSelectedOptions);
  }, [initialSelectedOptions]);

  useEffect(() => {
    setSelectedSort(initialSortBy);
  }, [initialSortBy]);

  useEffect(() => {
    setPriceRange(initialPriceRange);
    setMinPrice(initialPriceRange[0].toString());
    setMaxPrice(initialPriceRange[1].toString());
  }, [initialPriceRange]);

  useEffect(() => {
    setSelectedManufacturer(initialSelectedManufacturer);
    if (initialSelectedManufacturer) {
      setSearchBrand(initialSelectedManufacturer.name);
    } else {
      setSearchBrand('');
    }
  }, [initialSelectedManufacturer]);

  // Filtrar fabricantes cuando cambia la búsqueda
  useEffect(() => {
    if (searchBrand.trim() === '') {
      setFilteredManufacturers(manufacturers);
    } else {
      const filtered = manufacturers.filter(manufacturer =>
        manufacturer.name.toLowerCase().includes(searchBrand.toLowerCase())
      );
      setFilteredManufacturers(filtered);
    }
  }, [searchBrand, manufacturers]);

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleCategoryToggle = (categoryId: string) => {
    setSelectedCategories(prev => 
      prev.includes(categoryId) 
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const handleSortChange = (sortId: string) => {
    setSelectedSort(sortId);
  };

  // Handler para seleccionar fabricante
  const handleManufacturerSelect = useCallback((manufacturer: Manufacturer) => {
    setSelectedManufacturer(manufacturer);
    setSearchBrand(manufacturer.name);
    setIsDropdownOpen(false);
  }, []);

  // Handler para limpiar selección
  const handleClearManufacturer = useCallback(() => {
    setSelectedManufacturer(null);
    setSearchBrand('');
  }, []);

  // Validar y actualizar precio mínimo
  const handleMinPriceChange = useCallback((value: string) => {
    const numValue = parseInt(value) || 0;
    const maxValue = priceRange[1];
    
    // Validar límites
    if (numValue < 0) {
      setMinPrice('0');
      setPriceRange([0, maxValue]);
    } else if (numValue > 1000) {
      setMinPrice('1000');
      setPriceRange([1000, maxValue]);
    } else if (numValue > maxValue) {
      // Si el mínimo es mayor al máximo, intercambiar valores
      setMinPrice(value);
      setMaxPrice(numValue.toString());
      setPriceRange([numValue, numValue]);
    } else {
      setMinPrice(value);
      setPriceRange([numValue, maxValue]);
    }
  }, [priceRange]);

  // Validar y actualizar precio máximo
  const handleMaxPriceChange = useCallback((value: string) => {
    const numValue = parseInt(value) || 0;
    const minValue = priceRange[0];
    
    // Validar límites
    if (numValue < 0) {
      setMaxPrice('0');
      setPriceRange([minValue, 0]);
    } else if (numValue > 1000) {
      setMaxPrice('1000');
      setPriceRange([minValue, 1000]);
    } else if (numValue < minValue) {
      // Si el máximo es menor al mínimo, intercambiar valores
      setMaxPrice(value);
      setMinPrice(numValue.toString());
      setPriceRange([numValue, numValue]);
    } else {
      setMaxPrice(value);
      setPriceRange([minValue, numValue]);
    }
  }, [priceRange]);

  const handleApply = () => {
    onApply({
      categories: selectedCategories,
      sort: selectedSort,
      priceRange,
      manufacturer: selectedManufacturer
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div data-component-id="src/components/sections/mobile/ProductsFilterBarMobile.tsx:178:4" data-component-path="src/components/sections/mobile/ProductsFilterBarMobile.tsx" data-component-line="178" data-component-file="ProductsFilterBarMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-full%22%7D" className="w-full" style={{ backgroundColor: '#FEF5E6' }}>
      <div data-component-id="src/components/sections/mobile/ProductsFilterBarMobile.tsx:179:6" data-component-path="src/components/sections/mobile/ProductsFilterBarMobile.tsx" data-component-line="179" data-component-file="ProductsFilterBarMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-full%20flex%20flex-col%22%7D" className="w-full flex flex-col">
        {/* Encabezado interno eliminado para evitar duplicidad de breadcrumb y botón */}

        {/* Contenido del filtro */}
        <div data-component-id="src/components/sections/mobile/ProductsFilterBarMobile.tsx:183:8" data-component-path="src/components/sections/mobile/ProductsFilterBarMobile.tsx" data-component-line="183" data-component-file="ProductsFilterBarMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22px-4%20pb-4%22%7D" className="px-4 pb-4">
          {/* Filtrar por categoría */}
          <div data-component-id="src/components/sections/mobile/ProductsFilterBarMobile.tsx:185:10" data-component-path="src/components/sections/mobile/ProductsFilterBarMobile.tsx" data-component-line="185" data-component-file="ProductsFilterBarMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-6%22%7D" className="mb-6">
            <h3 data-component-id="src/components/sections/mobile/ProductsFilterBarMobile.tsx:186:12" data-component-path="src/components/sections/mobile/ProductsFilterBarMobile.tsx" data-component-line="186" data-component-file="ProductsFilterBarMobile.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22font-tt-norms-regular%20font-bold%20text-base%20leading-5%20mb-3%22%2C%22textContent%22%3A%22Filtrar%20por%20caracter%C3%ADsticas%22%7D" className="font-tt-norms-regular font-bold text-base leading-5 mb-3" style={{ 
              color: '#3D1C1A',
              fontFamily: "'TT_Norms_Pro-Regular', 'TT Norms Pro', 'Inter', sans-serif"
            }}>
              Filtrar por características
            </h3>
            <div data-component-id="src/components/sections/mobile/ProductsFilterBarMobile.tsx:192:12" data-component-path="src/components/sections/mobile/ProductsFilterBarMobile.tsx" data-component-line="192" data-component-file="ProductsFilterBarMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-3%20gap-3%22%7D" className="grid grid-cols-3 gap-3">
              {filterOptions.map((filter) => (
                <button data-component-id="src/components/sections/mobile/ProductsFilterBarMobile.tsx:194:16" data-component-path="src/components/sections/mobile/ProductsFilterBarMobile.tsx" data-component-line="194" data-component-file="ProductsFilterBarMobile.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
                  key={filter.value}
                  onClick={() => handleCategoryToggle(filter.value)}
                  className={`w-full text-center px-4 py-2 rounded-lg border text-sm leading-4 transition-colors ${
                    selectedCategories.includes(filter.value)
                      ? 'border-[#3D1C1A]'
                      : 'border-gray-300'
                  }`}
                  style={{ 
                    backgroundColor: selectedCategories.includes(filter.value) ? '#3D1C1A' : '#FEF5E6',
                    borderColor: selectedCategories.includes(filter.value) ? '#3D1C1A' : 'rgba(61, 28, 26, 0.3)',
                    color: selectedCategories.includes(filter.value) ? '#FFFFFF' : '#3D1C1A',
                    fontFamily: "'TT Norms Pro Medium', 'TT_Norms_Pro-Medium', 'TT Norms Pro', 'Inter', sans-serif"
                  }}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>

          {/* Filtrar por marca - oculto en mobile */}

          {/* Ordenar por */}
          <div data-component-id="src/components/sections/mobile/ProductsFilterBarMobile.tsx:218:10" data-component-path="src/components/sections/mobile/ProductsFilterBarMobile.tsx" data-component-line="218" data-component-file="ProductsFilterBarMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-6%22%7D" className="mb-6">
            <h3 data-component-id="src/components/sections/mobile/ProductsFilterBarMobile.tsx:219:12" data-component-path="src/components/sections/mobile/ProductsFilterBarMobile.tsx" data-component-line="219" data-component-file="ProductsFilterBarMobile.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22font-tt-norms-regular%20font-bold%20text-base%20leading-5%20mb-3%22%2C%22textContent%22%3A%22Ordenar%20por%22%7D" className="font-tt-norms-regular font-bold text-base leading-5 mb-3" style={{ 
              color: '#3D1C1A',
              fontFamily: "'TT_Norms_Pro-Regular', 'TT Norms Pro', 'Inter', sans-serif"
            }}>
              Ordenar por
            </h3>
            <div data-component-id="src/components/sections/mobile/ProductsFilterBarMobile.tsx:225:12" data-component-path="src/components/sections/mobile/ProductsFilterBarMobile.tsx" data-component-line="225" data-component-file="ProductsFilterBarMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-3%22%7D" className="space-y-3">
              {/* Resto en una sola columna */}
              <div data-component-id="src/components/sections/mobile/ProductsFilterBarMobile.tsx:227:14" data-component-path="src/components/sections/mobile/ProductsFilterBarMobile.tsx" data-component-line="227" data-component-file="ProductsFilterBarMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-3%22%7D" className="space-y-3">
                {sortOptions.map((option) => (
                  <label data-component-id="src/components/sections/mobile/ProductsFilterBarMobile.tsx:229:18" data-component-path="src/components/sections/mobile/ProductsFilterBarMobile.tsx" data-component-line="229" data-component-file="ProductsFilterBarMobile.tsx" data-component-name="label" data-component-content="%7B%22elementName%22%3A%22label%22%2C%22className%22%3A%22flex%20items-center%20gap-3%20cursor-pointer%22%7D" key={option.value} className="flex items-center gap-3 cursor-pointer">
                  <input data-component-id="src/components/sections/mobile/ProductsFilterBarMobile.tsx:230:18" data-component-path="src/components/sections/mobile/ProductsFilterBarMobile.tsx" data-component-line="230" data-component-file="ProductsFilterBarMobile.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22radio%22%2C%22name%22%3A%22sort%22%2C%22className%22%3A%22hidden%22%7D"
                    type="radio"
                    name="sort"
                    value={option.value}
                    checked={selectedSort === option.value}
                    onChange={() => handleSortChange(option.value)}
                    className="hidden"
                  />
                  <div data-component-id="src/components/sections/mobile/ProductsFilterBarMobile.tsx:238:18" data-component-path="src/components/sections/mobile/ProductsFilterBarMobile.tsx" data-component-line="238" data-component-file="ProductsFilterBarMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" 
                    className={`w-4 h-4 rounded-full border flex items-center justify-center ${
                      selectedSort === option.value 
                        ? 'bg-[#FEF5E6] border-[#3D1C1A]' 
                        : 'bg-[#FEF5E6] border-[#3D1C1A]'
                    }`}
                  >
                    {selectedSort === option.value && (
                      <div data-component-id="src/components/sections/mobile/ProductsFilterBarMobile.tsx:246:22" data-component-path="src/components/sections/mobile/ProductsFilterBarMobile.tsx" data-component-line="246" data-component-file="ProductsFilterBarMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-2%20h-2%20bg-%5B%233D1C1A%5D%20rounded-full%22%7D" className="w-2 h-2 bg-[#3D1C1A] rounded-full"></div>
                    )}
                  </div>
                    <span data-component-id="src/components/sections/mobile/ProductsFilterBarMobile.tsx:249:20" data-component-path="src/components/sections/mobile/ProductsFilterBarMobile.tsx" data-component-line="249" data-component-file="ProductsFilterBarMobile.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-tt-norms-regular%20font-normal%20text-sm%20leading-4%20whitespace-nowrap%22%7D" className="font-tt-norms-regular font-normal text-sm leading-4 whitespace-nowrap" style={{ 
                    color: '#3D1C1A',
                    fontFamily: "'TT_Norms_Pro-Regular', 'TT Norms Pro', 'Inter', sans-serif"
                  }}>
                    {option.label}
                  </span>
                </label>
              ))}
              </div>
            </div>
          </div>

          {/* Filtrar por precio */}
          <div data-component-id="src/components/sections/mobile/ProductsFilterBarMobile.tsx:262:10" data-component-path="src/components/sections/mobile/ProductsFilterBarMobile.tsx" data-component-line="262" data-component-file="ProductsFilterBarMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-6%22%7D" className="mb-6">
            <h3 data-component-id="src/components/sections/mobile/ProductsFilterBarMobile.tsx:263:12" data-component-path="src/components/sections/mobile/ProductsFilterBarMobile.tsx" data-component-line="263" data-component-file="ProductsFilterBarMobile.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22font-tt-norms-regular%20font-bold%20text-base%20leading-5%20mb-3%22%2C%22textContent%22%3A%22Filtrar%20por%20precio%22%7D" className="font-tt-norms-regular font-bold text-base leading-5 mb-3" style={{ 
              color: '#3D1C1A',
              fontFamily: "'TT_Norms_Pro-Regular', 'TT Norms Pro', 'Inter', sans-serif"
            }}>
              Filtrar por precio
            </h3>
            <div data-component-id="src/components/sections/mobile/ProductsFilterBarMobile.tsx:269:12" data-component-path="src/components/sections/mobile/ProductsFilterBarMobile.tsx" data-component-line="269" data-component-file="ProductsFilterBarMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-4%22%7D" className="space-y-4">
              {/* Campos de precio */}
              <div data-component-id="src/components/sections/mobile/ProductsFilterBarMobile.tsx:271:14" data-component-path="src/components/sections/mobile/ProductsFilterBarMobile.tsx" data-component-line="271" data-component-file="ProductsFilterBarMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-2%22%7D" className="flex items-center gap-2">
                <div data-component-id="src/components/sections/mobile/ProductsFilterBarMobile.tsx:272:16" data-component-path="src/components/sections/mobile/ProductsFilterBarMobile.tsx" data-component-line="272" data-component-file="ProductsFilterBarMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%20relative%22%7D" className="flex-1 relative">
                  <input data-component-id="src/components/sections/mobile/ProductsFilterBarMobile.tsx:273:18" data-component-path="src/components/sections/mobile/ProductsFilterBarMobile.tsx" data-component-line="273" data-component-file="ProductsFilterBarMobile.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22number%22%2C%22value%22%3A%22%5Bvar%3AminPrice%5D%22%2C%22className%22%3A%22w-full%20px-3%20py-2%20pr-8%20rounded-lg%20text-sm%20leading-4%20text-center%22%7D"
                    type="number"
                    value={minPrice}
                    onChange={(e) => handleMinPriceChange(e.target.value)}
                    className="w-full px-3 py-2 pr-8 rounded-lg text-sm leading-4 text-center"
                    style={{ 
                      backgroundColor: 'rgba(61, 28, 26, 0.15)',
                      borderColor: 'rgba(61, 28, 26, 0.3)',
                      color: '#3D1C1A',
                      fontFamily: "'TT Norms Pro Medium', 'TT_Norms_Pro-Medium', 'TT Norms Pro', 'Inter', sans-serif"
                    }}
                    placeholder="0€"
                    min="0"
                    max="1000"
                  />
                  <span data-component-id="src/components/sections/mobile/ProductsFilterBarMobile.tsx:288:18" data-component-path="src/components/sections/mobile/ProductsFilterBarMobile.tsx" data-component-line="288" data-component-file="ProductsFilterBarMobile.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22absolute%20right-3%20top-1%2F2%20transform%20-translate-y-1%2F2%20text-sm%20pointer-events-none%22%2C%22textContent%22%3A%22%E2%82%AC%22%7D" className="absolute right-3 top-1/2 transform -translate-y-1/2 text-sm pointer-events-none" style={{ 
                    color: '#3D1C1A',
                    fontFamily: "'TT Norms Pro Medium', 'TT_Norms_Pro-Medium', 'TT Norms Pro', 'Inter', sans-serif"
                  }}>€</span>
                </div>
                <span data-component-id="src/components/sections/mobile/ProductsFilterBarMobile.tsx:293:16" data-component-path="src/components/sections/mobile/ProductsFilterBarMobile.tsx" data-component-line="293" data-component-file="ProductsFilterBarMobile.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22-%22%7D" style={{ 
                  color: '#3D1C1A',
                  fontFamily: "'TT Norms Pro Medium', 'TT_Norms_Pro-Medium', 'TT Norms Pro', 'Inter', sans-serif"
                }}>-</span>
                <div data-component-id="src/components/sections/mobile/ProductsFilterBarMobile.tsx:297:16" data-component-path="src/components/sections/mobile/ProductsFilterBarMobile.tsx" data-component-line="297" data-component-file="ProductsFilterBarMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%20relative%22%7D" className="flex-1 relative">
                  <input data-component-id="src/components/sections/mobile/ProductsFilterBarMobile.tsx:298:18" data-component-path="src/components/sections/mobile/ProductsFilterBarMobile.tsx" data-component-line="298" data-component-file="ProductsFilterBarMobile.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22number%22%2C%22value%22%3A%22%5Bvar%3AmaxPrice%5D%22%2C%22className%22%3A%22w-full%20px-3%20py-2%20pr-8%20rounded-lg%20text-sm%20leading-4%20text-center%22%7D"
                    type="number"
                    value={maxPrice}
                    onChange={(e) => handleMaxPriceChange(e.target.value)}
                    className="w-full px-3 py-2 pr-8 rounded-lg text-sm leading-4 text-center"
                    style={{ 
                      backgroundColor: 'rgba(61, 28, 26, 0.15)',
                      borderColor: 'rgba(61, 28, 26, 0.3)',
                      color: '#3D1C1A',
                      fontFamily: "'TT Norms Pro Medium', 'TT_Norms_Pro-Medium', 'TT Norms Pro', 'Inter', sans-serif"
                    }}
                    placeholder="1000€"
                    min="0"
                    max="1000"
                  />
                  <span data-component-id="src/components/sections/mobile/ProductsFilterBarMobile.tsx:313:18" data-component-path="src/components/sections/mobile/ProductsFilterBarMobile.tsx" data-component-line="313" data-component-file="ProductsFilterBarMobile.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22absolute%20right-3%20top-1%2F2%20transform%20-translate-y-1%2F2%20text-sm%20pointer-events-none%22%2C%22textContent%22%3A%22%E2%82%AC%22%7D" className="absolute right-3 top-1/2 transform -translate-y-1/2 text-sm pointer-events-none" style={{ 
                    color: '#3D1C1A',
                    fontFamily: "'TT Norms Pro Medium', 'TT_Norms_Pro-Medium', 'TT Norms Pro', 'Inter', sans-serif"
                  }}>€</span>
                </div>
              </div>
              
              {/* Slider de precio */}
              <div data-component-id="src/components/sections/mobile/ProductsFilterBarMobile.tsx:321:14" data-component-path="src/components/sections/mobile/ProductsFilterBarMobile.tsx" data-component-line="321" data-component-file="ProductsFilterBarMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22px-2%22%7D" className="px-2">
                <Range data-component-id="src/components/sections/mobile/ProductsFilterBarMobile.tsx:322:16" data-component-path="src/components/sections/mobile/ProductsFilterBarMobile.tsx" data-component-line="322" data-component-file="ProductsFilterBarMobile.tsx" data-component-name="Range" data-component-content="%7B%22elementName%22%3A%22Range%22%7D"
                  step={1}
                  min={0}
                  max={1000}
                  values={priceRange}
                  onChange={(values) => {
                    setPriceRange(values as [number, number]);
                    setMinPrice(values[0].toString());
                    setMaxPrice(values[1].toString());
                  }}
                  renderTrack={({ props, children }) => (
                    <div data-component-id="src/components/sections/mobile/ProductsFilterBarMobile.tsx:333:20" data-component-path="src/components/sections/mobile/ProductsFilterBarMobile.tsx" data-component-line="333" data-component-file="ProductsFilterBarMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22%5Bspread%5D%22%3A%22true%22%2C%22className%22%3A%22flex-1%20h-2%20bg-%5B%233D1C1A%5D%20relative%20rounded-full%20opacity-20%22%7D"
                      {...props}
                      className="flex-1 h-2 bg-[#3D1C1A] relative rounded-full opacity-20"
                      style={{
                        ...props.style,
                      }}
                    >
                      <div data-component-id="src/components/sections/mobile/ProductsFilterBarMobile.tsx:340:22" data-component-path="src/components/sections/mobile/ProductsFilterBarMobile.tsx" data-component-line="340" data-component-file="ProductsFilterBarMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20top-0%20h-full%20bg-%5B%233D1C1A%5D%20rounded-full%22%7D"
                        className="absolute top-0 h-full bg-[#3D1C1A] rounded-full"
                        style={{
                          left: `${(priceRange[0] / 1000) * 100}%`,
                          width: `${((priceRange[1] - priceRange[0]) / 1000) * 100}%`,
                        }}
                      />
                      {children}
                    </div>
                  )}
                  renderThumb={({ props }) => {
                    const { key, ...otherProps } = props;
                    return (
                      <div data-component-id="src/components/sections/mobile/ProductsFilterBarMobile.tsx:353:22" data-component-path="src/components/sections/mobile/ProductsFilterBarMobile.tsx" data-component-line="353" data-component-file="ProductsFilterBarMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22%5Bspread%5D%22%3A%22true%22%2C%22className%22%3A%22w-4%20h-4%20bg-%5B%23FEF5E6%5D%20border-2%20border-%5B%233D1C1A%5D%20rounded-full%20cursor-pointer%20hover%3Ascale-110%20transition-transform%22%7D"
                        key={key}
                        {...otherProps}
                        className="w-4 h-4 bg-[#FEF5E6] border-2 border-[#3D1C1A] rounded-full cursor-pointer hover:scale-110 transition-transform"
                        style={{
                          ...otherProps.style,
                        }}
                      />
                    );
                  }}
                />
                <div data-component-id="src/components/sections/mobile/ProductsFilterBarMobile.tsx:364:16" data-component-path="src/components/sections/mobile/ProductsFilterBarMobile.tsx" data-component-line="364" data-component-file="ProductsFilterBarMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20justify-between%20mt-2%22%7D" className="flex justify-between mt-2">
                  <span data-component-id="src/components/sections/mobile/ProductsFilterBarMobile.tsx:365:18" data-component-path="src/components/sections/mobile/ProductsFilterBarMobile.tsx" data-component-line="365" data-component-file="ProductsFilterBarMobile.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-xs%22%2C%22textContent%22%3A%220%E2%82%AC%22%7D" className="text-xs" style={{ 
                    color: '#3D1C1A',
                    fontFamily: "'TT Norms Pro Medium', 'TT_Norms_Pro-Medium', 'TT Norms Pro', 'Inter', sans-serif"
                  }}>0€</span>
                  <span data-component-id="src/components/sections/mobile/ProductsFilterBarMobile.tsx:369:18" data-component-path="src/components/sections/mobile/ProductsFilterBarMobile.tsx" data-component-line="369" data-component-file="ProductsFilterBarMobile.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-xs%22%2C%22textContent%22%3A%221000%E2%82%AC%22%7D" className="text-xs" style={{ 
                    color: '#3D1C1A',
                    fontFamily: "'TT Norms Pro Medium', 'TT_Norms_Pro-Medium', 'TT Norms Pro', 'Inter', sans-serif"
                  }}>1000€</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Botón aplicar */}
        <div data-component-id="src/components/sections/mobile/ProductsFilterBarMobile.tsx:380:8" data-component-path="src/components/sections/mobile/ProductsFilterBarMobile.tsx" data-component-line="380" data-component-file="ProductsFilterBarMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22p-4%20border-t%22%7D" className="p-4 border-t" style={{ borderColor: 'rgba(61, 28, 26, 0.2)' }}>
          <button data-component-id="src/components/sections/mobile/ProductsFilterBarMobile.tsx:381:10" data-component-path="src/components/sections/mobile/ProductsFilterBarMobile.tsx" data-component-line="381" data-component-file="ProductsFilterBarMobile.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22w-full%20py-3%20rounded-lg%20font-tt-norms-regular%20font-bold%20text-base%20leading-5%22%2C%22textContent%22%3A%22Aplicar%22%7D"
            onClick={handleApply}
            className="w-full py-3 rounded-lg font-tt-norms-regular font-bold text-base leading-5"
            style={{ 
              backgroundColor: '#3D1C1A', 
              color: '#FFFFFF',
              fontFamily: "'TT_Norms_Pro-Regular', 'TT Norms Pro', 'Inter', sans-serif"
            }}
          >
            Aplicar
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductsFilterBarMobile; 